import type { WireEvidenceProposal, WireMarketRow, WireMarketSource, WirePlan, WireProfile, WireSignal, WireTerritory, WireTool } from '@/api/wire';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';
import { countryName, dayWords, signalWords } from '@/ui/words';
import { toolName } from '@/app/technical';
import { STAGE_WORD, contactWords, eur, shortDate } from './words';

/**
 * What each record of the prospecting screens says about itself (ADR-0155):
 * markets, a market's sources and proposed findings, signals, profiles,
 * territories and plans. Each builder turns a row the screen already holds
 * into an `ItemContext`; none reads anything else, and none asks a model.
 *
 * The rules they keep are the screens' own: a plan searches only once a
 * superadmin has approved it; what an assistant proposes waits for a person;
 * nothing here is sent by the platform; a source reads nothing until its
 * reader is signed, admitted and granted. A field the row does not hold is
 * left out or said to be unrecorded, never guessed (§18: a department's own
 * lists only, and no address, id or code reaches a sentence).
 */

/** The reader, as the builders need them: where they work, and whether a decision is theirs. */
export interface Reader {
  department: string;
  /** Lead of their department, or a superadmin: the people who decide here. */
  decides: boolean;
  humanId: string;
  /** How a record that names people by name names them: an introduction's colleague. */
  displayName?: string | undefined;
}

export const readerOf = (me: { department: string; isSuperadmin: boolean; leads: string[]; humanId: string; displayName?: string }): Reader => ({
  department: me.department,
  decides: me.isSuperadmin || me.leads.includes(me.department),
  humanId: me.humanId,
  displayName: me.displayName,
});

const DAY = 86_400_000;

/** Whole days since an ISO time; never negative. */
export const daysSince = (iso: string, now: number): number => Math.max(0, Math.floor((now - Date.parse(iso)) / DAY));

export const sinceWords = (iso: string, now: number): string => {
  const d = daysSince(iso, now);
  return d <= 0 ? 'today' : d === 1 ? 'a day ago' : `${d} days ago`;
};

export const clip = (text: string, to = 80): string => (text.length > to ? `${text.slice(0, to - 1).trimEnd()}…` : text);

const CODEY = /[0-9a-f]{8}-[0-9a-f]{4}-|@|[a-z0-9]+_[a-z0-9_]+/i;

/** Words a person wrote, or null for text that looks like an id, an address or a code: it is not shown. */
export function say(text: string | null | undefined): string | null {
  if (!text) return null;
  const t = text.trim();
  return t && !CODEY.test(t) ? t : null;
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

export const withRole = (parties: Party[], role: string): Party[] => parties.map((p) => ({ ...p, role }));

/** The department's agent, when it has exactly one: only then is "the agent that did it" not a guess. */
export function soleAgent(who: Who, department: string, role: string): Party | null {
  const agents = who.agentsOf(department);
  return agents.length === 1 ? { ...agents[0]!, role } : null;
}

/** A person's name for a colleague recorded by name; the reader reads "You". */
export function byName(name: string | null | undefined, humanId: string | null | undefined, reader: Reader): Party | null {
  if (!name) return null;
  return humanId && humanId === reader.humanId ? { name: 'You', kind: 'person' } : { name, kind: 'person' };
}

/**
 * The person whose press wrote a record, named as the platform kept them
 * (0162), with what they did: `undefined` on an older reply without the
 * field, `null` when nobody was recorded — never guessed.
 */
export function signedInBy(p: { proposedBy?: string | null | undefined; proposedByHuman?: string | undefined }, reader: Reader): string | null {
  return byName(p.proposedBy, p.proposedByHuman, reader)?.name ?? null;
}

export function pressedBy(name: string | null | undefined, humanId: string | null | undefined, reader: Reader, role: string, when?: string | null): ItemContext['broughtBy'] {
  if (name === undefined) return undefined;
  const party = byName(name, humanId, reader);
  return party ? { ...party, role, ...(when ? { when } : {}) } : null;
}

const COUNTRY = (code: string) => countryName(code);

/**
 * Who made a record, from the address the platform kept (0162): `undefined`
 * on an older reply that did not carry the field, so the row is left out;
 * `null` when the record names nobody, which the panel reads as not
 * recorded. Never guessed.
 */
export function madeBy(who: Who, address: string | null | undefined, role: string): ItemContext['broughtBy'] {
  if (address === undefined) return undefined;
  const party = who.actor(address);
  return party ? { ...party, role } : null;
}

// --- Markets ---------------------------------------------------------------------

/**
 * A market, one per country (ADR-0134). Go and not now are a person's: the
 * department's lead or a superadmin, in their own words. Nothing is
 * prospected in a country without a go.
 */
export function explainMarket(m: WireMarketRow, who: Who, reader: Reader): ItemContext {
  const country = m.country || COUNTRY(m.marketCode);
  const lead = who.lead(reader.department);
  const open = 11 - m.criteriaDone;
  const writing = contactWords(m.contact).toLowerCase();
  const base = {
    subject: { kind: 'market', title: country },
    owner: lead,
    knows: who.knowers(reader.department),
    open: { to: `/markets/${m.marketCode}` },
  } satisfies Partial<ItemContext>;
  const facts: ItemContext['facts'] = [
    ['Rank', m.screenRank !== null ? `${m.screenRank}${m.screenScore !== null ? ` · score ${m.screenScore} of 100` : ''}` : null],
    ['Deep-scan criteria', m.stage === 'deep' || m.stage === 'go' || m.stage === 'not_now' ? `${m.criteriaDone} of 11 have a finding` : null],
    ['Writing to people there', contactWords(m.contact)],
    ['Decided', m.decidedAt && (m.stage === 'go' || m.stage === 'not_now') ? `${STAGE_WORD[m.stage]} on ${dayWords(m.decidedAt)}` : null],
  ];

  if (m.stage === null) {
    return {
      ...base,
      summary: `${country} is named in the markets file, but nobody has researched it yet, so it has no rank, no findings and no price. Writing to people there is ${writing}.`,
      need: 'Research: someone asks Nia to screen it. Each finding comes back with its source.',
      why: 'The markets file declares it, so it is listed beside the countries that have been researched.',
      source: { label: 'The markets file', to: undefined },
      broughtBy: null,
      involved: withRole(who.agentsOf('executive'), 'researches markets'),
      facts,
      next: reader.decides
        ? {
            tip: `Open ${country} and ask Nia to research it. Nothing is prospected in a country until a person has called it go, so research comes first.`,
            instead: 'If you do not want to enter it, leave it: nothing happens to a country nobody researches.',
          }
        : {
            tip: `Nothing is needed from you. ${lead && lead.name !== 'You' ? `${lead.name} decides whether ${country} is researched.` : 'The department’s lead decides whether it is researched.'}`,
            instead: 'If you know the country is worth a look, tell the lead why.',
          },
    };
  }

  const researched = {
    source: { label: 'The screen on size, economics and trade, then Nia’s deep scan for the few worth it' },
    involved: withRole(who.agentsOf('executive'), 'researches markets'),
    facts,
  };

  if (m.stage === 'screened') {
    return {
      ...base,
      ...researched,
      summary: `${country} was ranked${m.screenRank !== null ? ` ${m.screenRank}${m.screenScore !== null ? ` with ${m.screenScore} points` : ''}` : ''} on size, economics and trade and not studied further.`,
      need: 'A deep scan before anyone can call it go or not now.',
      why: 'Every country the screen covers is listed so the ranking can be read in one place.',
      next: reader.decides
        ? {
            tip: `If ${country} interests you, open it and ask Nia for the deep scan; a go or not now cannot be recorded until it has one.`,
            instead: 'Otherwise leave it as it is: a screened country costs nothing.',
          }
        : { tip: 'Nothing is needed from you. A screened country waits for its lead to ask for a deep scan.', instead: 'Tell the lead if you have a reason to look at it.' },
    };
  }

  if (m.stage === 'deep') {
    const rec = m.recommendationStage ? STAGE_WORD[m.recommendationStage].toLowerCase() : null;
    return {
      ...base,
      ...researched,
      summary: `${country} has a deep scan under way: ${m.criteriaDone} of 11 criteria have a finding${rec ? `, and the research recommends “${rec}”` : ''}. No person has decided yet.`,
      need: reader.decides ? 'Your decision: go or not now, in your own words.' : 'A decision from the department’s lead.',
      why: `A market ends with a person’s go or not now; this one is waiting for it${open > 0 ? `, with ${plural(open, 'criterion', 'criteria')} still open` : ''}.`,
      next: reader.decides
        ? {
            tip: open > 0 ? `Read the findings and ask Nia for the ${plural(open, 'open criterion', 'open criteria')} first, then decide go or not now from its page.` : `Read the findings and decide go or not now from its page; the recommendation is not copied into your words.`,
            instead: 'If you are not ready, leave it undecided: nothing is prospected in a country without a go.',
          }
        : { tip: `Nothing is needed from you. ${lead && lead.name !== 'You' ? lead.name : 'The lead'} reads the findings and decides.`, instead: 'You can read the findings on its page meanwhile.' },
    };
  }

  const decidedBy = who.actor(m.decidedBy ?? null);
  const decided = {
    ...base,
    source: { label: 'The deep scan, and a person’s decision on it', ...(m.decidedAt ? { when: dayWords(m.decidedAt) } : {}) },
    broughtBy: decidedBy ? { ...decidedBy, ...(m.decidedAt ? { when: dayWords(m.decidedAt) } : {}) } : null,
    involved: withRole(who.agentsOf('executive'), 'researches markets'),
    facts,
  };
  if (m.stage === 'go') {
    const blocked = !m.contact.declared || !m.contact.enabled;
    return {
      ...decided,
      summary: `${country} is go${decidedBy ? `, decided by ${decidedBy.name}` : ''}. A plan may now be set up there${blocked ? `, though writing to people is ${writing}` : ''}.`,
      need: reader.decides ? 'A profile, a territory and a plan, if prospecting here is wanted.' : undefined,
      why: 'A person called it go, so it is the place the department’s prospecting can now be pointed at.',
      next: reader.decides
        ? {
            tip: blocked
              ? `Set up prospecting here, but note writing to people in ${country} is ${writing}: the markets file decides that, not research, so nobody there can be written to yet.`
              : `Set up prospecting here: a profile, a territory and a plan. The plan waits for a superadmin to approve it before anything is searched.`,
            instead: 'Or leave it go without a plan: nothing is searched until one is approved.',
            actions: [{ label: 'Set up prospecting here', to: `/prospecting/setup?market=${m.marketCode}`, primary: true }],
          }
        : { tip: 'Nothing is needed from you. Prospecting here runs under a plan the superadmin approves.', instead: 'Its prospects appear under Prospects as they are found.' },
    };
  }
  return {
    ...decided,
    summary: `${country} is not now${decidedBy ? `, decided by ${decidedBy.name}` : ''}. Nothing is prospected there.`,
    why: 'A person decided against entering it for now, and the decision stays on the record.',
    next: reader.decides
      ? { tip: 'Nothing to do. If research later changes the picture, ask Nia to research it again and record a new decision from its page.', instead: 'Leaving it is also a decision: nothing is prospected here.' }
      : { tip: 'Nothing is needed from you.', instead: 'It is reconsidered only when its lead records a new decision.' },
  };
}

// --- A market's own sources --------------------------------------------------------

const KIND_WORD: Record<string, string> = {
  news: 'a news site',
  blog: 'a blog',
  statistics: 'statistics',
  register: 'a register',
  tax_office: 'a tax office',
  trade_body: 'a trade body',
  other: 'a source',
};

/**
 * A source a person attached for a market (ADR-0141). It reads nothing until
 * its reader is signed, admitted and granted; a superadmin sees where it
 * stands, everyone else is told who can move it.
 */
export function explainSource(s: WireMarketSource, tool: WireTool | undefined, who: Who, reader: Reader, country: string): ItemContext {
  const signals = 'signals' in s.readsFor;
  const standing = !tool || !tool.indexed ? 'unsigned' : !tool.standing?.admitted ? 'not_admitted' : 'admitted';
  const reads =
    'signals' in s.readsFor
      ? `signals (${s.readsFor.signals.map((k) => signalWords(k).toLowerCase()).join(', ')})`
      : `research criteria (${s.readsFor.criteria.join(', ').toLowerCase()})`;
  const sole = signals ? soleAgent(who, reader.department, 'reads it for signals') : null;
  const researcher = who.agentsOf('executive');
  const adder = who.actor(s.addedBy);
  const last = s.lastRead;
  const failing = last !== null && !last.ok;
  const word = who.superadmin ? 'manifest' : 'reader';

  let tip: string;
  let instead: string | undefined;
  let actions: ItemContext['next']['actions'];
  if (standing !== 'admitted') {
    tip = who.superadmin
      ? `Its ${word} is ${standing === 'unsigned' ? 'not signed' : 'not admitted'}, so nothing is read from it. ${standing === 'unsigned' ? 'Sign it, then admit it' : 'Admit it'} on the Tool library; until then this source reads nothing.`
      : 'Nothing is read from it yet: a superadmin has to switch its reader on. Ask them; there is nothing for you to do.';
    instead = 'Or remove the line from the market’s sources file if it is no longer wanted.';
    if (who.superadmin) actions = [{ label: 'Open the Tool library', to: '/tools' }];
  } else if (failing) {
    tip = `The last read on ${shortDate(last.at)} did not answer. Open the source in your browser to check it still exists; if it moved, a superadmin corrects its line.`;
    instead = 'If it has gone for good, it can be taken out of the sources file.';
  } else if (!last) {
    tip = `Nothing is needed. It is admitted and has not been read yet; it is read every ${plural(s.everyDays, 'day')}.`;
  } else if (s.signalsTotal > 0) {
    tip = `It has left ${plural(s.signalsTotal, 'signal')} so far. Read them under Signals and act on the ones that matter.`;
    actions = [{ label: 'Open Signals', to: '/signals' }];
  } else {
    tip = `Nothing is needed. It was read ${shortDate(last.at)} and found nothing new for ${country}; it is read again every ${plural(s.everyDays, 'day')}.`;
  }

  return {
    subject: { kind: 'source', title: s.title },
    summary: `${s.title} is ${KIND_WORD[s.kind] ?? 'a source'} a person attached for ${country}. It is read ${s.read === 'feed' ? 'as a feed of items' : 'as a page, whole'} for ${reads}.`,
    need: standing === 'admitted' ? 'Nothing from you while it is read and answers.' : 'A superadmin to sign, admit and grant its reader before it reads anything.',
    why: `A market’s own sources are where an agent may read about ${country}; nothing is read that no line names.`,
    source: { label: who.superadmin ? 'A line a person committed in the market’s sources file' : 'A source a person attached for this market', when: dayWords(s.addedOn) },
    broughtBy: adder ? { ...adder, when: dayWords(s.addedOn) } : null,
    owner: who.lead(reader.department),
    involved: signals ? (sole ? [sole] : withRole(who.agentsOf(reader.department), 'reads it for signals')) : withRole(researcher, 'reads it for research'),
    knows: [...(adder && adder.name !== 'You' ? [adder] : []), ...who.knowers(reader.department)],
    facts: [
      ['Read every', plural(s.everyDays, 'day')],
      ['Last read', last ? `${shortDate(last.at)} — ${last.ok ? `${plural(last.items, 'item')}, ${plural(last.signals, 'new signal')}` : 'did not answer'}` : 'Never read yet'],
      ['Signals so far', String(s.signalsTotal)],
      ['Its note', say(s.note)],
      ['Reader stands', who.superadmin ? { unsigned: 'Unsigned', not_admitted: 'Signed, not admitted', admitted: 'Admitted' }[standing] : null],
      ['Reader', who.superadmin ? toolName(s.tool) : null],
    ],
    next: { tip, ...(instead ? { instead } : {}), ...(actions ? { actions } : {}) },
  };
}

// --- A finding an assistant proposed -----------------------------------------------

/**
 * What an assistant a person signed in found for a market (ADR-0153). It
 * goes on record only when a person confirms, and then only as its own pages
 * bear it out.
 */
export function explainFinding(p: WireEvidenceProposal, who: Who, reader: Reader, country: string): ItemContext {
  const pages = plural(p.sources.length, 'cited page');
  const base = {
    subject: { kind: 'finding', title: clip(p.finding, 60) },
    source: { label: `${p.viaClient ? `Proposed by ${p.viaClient}, an assistant a person signed in` : 'Proposed by an assistant a person signed in'}, with ${pages}`, when: shortDate(p.createdAt) ?? undefined },
    broughtBy: { name: p.viaClient ?? 'An assistant', kind: 'agent' as const, role: 'an assistant a person signed in', when: shortDate(p.createdAt) ?? undefined },
    owner: who.lead(reader.department),
    involved: withRole(who.agentsOf('executive'), 'researches markets'),
    knows: who.knowers(reader.department),
    facts: [['Criterion', say(p.criterion)], ['Pages cited', String(p.sources.length)], ['Signed in by', signedInBy(p, reader)]] as ItemContext['facts'],
  };
  const summary = `A finding about ${country}, “${clip(p.finding, 140)}”, proposed under “${p.criterion}”.`;
  switch (p.state) {
    case 'proposed':
      return {
        ...base,
        summary,
        need: reader.decides ? 'Your confirmation, or your decline.' : 'The department’s lead to confirm or decline it.',
        why: 'An assistant’s proposal does nothing until a person confirms it, so it waits here.',
        next: reader.decides
          ? {
              tip: `Read what it says and open ${p.sources.length === 1 ? 'the page it cites' : 'the pages it cites'}. Confirm it and the department’s site reader reads those pages: it goes on record as sourced only where a page says it, otherwise as unverified.`,
              instead: 'Decline it if you do not trust it: nothing is kept.',
            }
          : { tip: 'Nothing is needed from you. The department’s lead confirms or declines it.', instead: 'You can open its cited pages yourself and tell the lead what you find.' },
      };
    case 'confirmed':
      return { ...base, summary, why: 'It was confirmed, so its pages are being read.', next: { tip: 'Nothing is needed from you. Its pages are being read now; it goes on record as sourced or unverified when that is done.' } };
    case 'sourced':
      return { ...base, summary, why: 'A person confirmed it and its own page bore it out.', next: { tip: 'Nothing to do: it is on the market’s record with its source.' } };
    case 'unverified':
      return {
        ...base,
        summary,
        why: 'A person confirmed it, but its cited pages did not say it.',
        next: {
          tip: reader.decides ? 'It is on record as unverified. Read the cited pages yourself and, if you find the figure, record the finding with its source from the market’s page.' : 'Nothing is needed from you. It stays on record as unverified until someone reads it back.',
          instead: 'Leave it unverified: the report says so.',
        },
      };
    default:
      return { ...base, summary, why: 'A person declined it, so nothing was kept.', next: { tip: 'Nothing to do.' } };
  }
}

// --- Signals -----------------------------------------------------------------------

/**
 * A signal: what was seen, of whom, where it was read, and the points it adds
 * (ADR-0134, ADR-0141). It is the words that were seen, never a model's
 * summary; a person reads the page at the link before acting.
 */
export function explainSignal(s: WireSignal, who: Who, reader: Reader): ItemContext {
  const country = COUNTRY(s.marketCode);
  const chef = s.source?.startsWith('contact:') === true;
  const feed = Boolean(s.sourceId);
  const subjectName = s.prospect ?? (s.prospectId ? 'a prospect whose name is not written down yet' : `the ${country} market`);
  const agent = chef ? null : soleAgent(who, reader.department, 'reads sites for the department');
  const sourceWords = chef ? 'A contact we know has moved to a prospect' : feed ? `One of ${country}’s own sources` : say(s.source) ?? 'Read off the prospect’s own site';
  const noPoints = s.points === 0;
  return {
    subject: { kind: 'signal', title: clip(s.said, 70) },
    summary: `${signalWords(s.kind)} about ${subjectName}, seen on ${shortDate(s.seenOn)}: “${clip(s.said, 160)}”. ${noPoints ? 'It adds no points: it is a reason not to write, never a score.' : `It adds ${s.points} ${s.points === 1 ? 'point' : 'points'} to the fit.`}`,
    need: noPoints ? 'A look before anyone writes to them.' : 'A person to read the page it came from and decide whether now is the time.',
    why: noPoints ? 'Something suggests this is the wrong moment, so it is shown beside the prospect.' : `It makes now a better time to write to ${s.prospect ?? 'them'}.`,
    source: { label: sourceWords, ...(feed ? { to: `/markets/${s.marketCode}` } : {}), when: shortDate(s.seenOn) ?? undefined },
    broughtBy: chef ? { name: 'The platform', kind: 'system', when: shortDate(s.seenOn) ?? undefined } : agent ? { ...agent, when: shortDate(s.seenOn) ?? undefined } : null,
    owner: who.lead(reader.department),
    involved: agent ? [agent] : undefined,
    knows: who.knowers(reader.department),
    facts: [
      ['Kind', signalWords(s.kind)],
      ['About', s.prospect],
      ['Country', country],
      ['Read at', s.url ?? null],
    ],
    next: noPoints
      ? {
          tip: s.prospectId
            ? 'Open the page it was read at, and if it still stands do not write to them for now; mark them lost with a reason from their record if they are not coming back.'
            : 'Read the page it came from; nothing is needed unless it changes how you see the market.',
          instead: 'If the page is out of date, ignore it: a signal without points never changes a score.',
          ...(s.prospectId ? { actions: [{ label: 'Open the prospect', to: `/prospects/${s.prospectId}` }] } : {}),
        }
      : s.prospectId
        ? {
            tip: `Read the page it was seen on, then open ${s.prospect ?? 'the prospect'} and see where it stands: if a first email is waiting as a draft, this is a reason to send it now; if not, there is nothing to do yet.`,
            instead: 'If it does not change anything for you, leave it: it has already counted towards the fit.',
            actions: [{ label: 'Open the prospect', to: `/prospects/${s.prospectId}`, primary: true }],
          }
        : {
            tip: 'It is about the market, not one prospect. Read the page it came from; if it changes how a plan should look here, tell the department’s lead.',
            instead: 'Otherwise nothing is needed.',
          },
    open: { to: `/signals/${s.id}` },
    task: s.points > 0 && s.prospectId ? { title: `Read the signal about ${s.prospect ?? 'a prospect'} and act on it`, from: `/signals/${s.id}` } : undefined,
  };
}

// --- Profiles ------------------------------------------------------------------------

const PROFILE_STATE: Record<WireProfile['status'], string> = { draft: 'a draft', live: 'live', retired: 'retired' };

/**
 * A profile: who we look for in a market, how to recognise them on Google,
 * and the rules that score a place's fit. A draft is the lead's to put live;
 * a live one is never edited, a change is proposed and a person decides it.
 */
export function explainProfile(p: WireProfile, who: Who, reader: Reader, country: string): ItemContext {
  const most = p.rules.reduce((n, r) => n + r.points, 0);
  const stuck = most < p.fitThreshold;
  const seeded = p.seededFrom.map(say).filter((x): x is string => x !== null);
  const base = {
    subject: { kind: 'profile', title: `${p.segment} in ${country}` },
    summary: `${p.segment} in ${country}, ${PROFILE_STATE[p.status]}, version ${p.version}. A place that meets ${p.fitThreshold} points of its ${plural(p.rules.length, 'rule')} (at most ${most}) is offered to a person.`,
    source: { label: seeded.length > 0 ? `Seeded from ${seeded.join(', ')}` : p.version > 1 ? `Version ${p.version}, written when a person decided a proposed change` : 'Written by Set up prospecting, as a draft for a market a person called go', to: undefined },
    broughtBy: madeBy(who, p.createdBy, p.version > 1 ? 'decided this version' : 'set it up'),
    owner: who.lead(reader.department),
    involved: withRole(who.agentsOf(reader.department), 'proposes changes from what converted'),
    knows: who.knowers(reader.department),
    facts: [
      ['Plans using it', String(p.plans)],
      ['Looks for', p.titles.join(', ') || null],
      ['Offered from', `${p.fitThreshold} points`],
    ] as ItemContext['facts'],
    open: { to: `/profiles/${p.id}` },
  };
  if (p.status === 'retired') {
    return { ...base, why: 'It is kept for the record of what past plans looked for.', next: { tip: 'Nothing to do. A retired profile is not used by new plans.', instead: 'To look for the same kind again, set up a new profile.' } };
  }
  if (p.status === 'draft') {
    return {
      ...base,
      need: reader.decides ? (stuck ? 'Its rules or its bar fixed, then your decision to make it live.' : 'Your decision to make it live.') : 'The department’s lead to make it live.',
      why: 'A draft changes nothing until it is live, and only the lead or a superadmin puts it live.',
      next: reader.decides
        ? stuck
          ? {
              tip: `Do not make it live yet: its rules give at most ${most} points but it offers a place from ${p.fitThreshold}, so nothing it finds would ever be offered to a person. Add a rule or lower the bar first.`,
              instead: 'Or retire this draft and set up a new profile.',
            }
          : { tip: 'Read how it recognises a place and how it scores one on its page, then make it live. After that it changes only when a person decides a proposed change.', instead: 'Leave it as a draft while you are unsure: no plan can use it.' }
        : { tip: 'Nothing is needed from you. The department’s lead reads it and puts it live.', instead: 'Tell the lead if a kind of place is missing from it.' },
    };
  }
  return {
    ...base,
    why: p.plans > 0 ? `It is in use by ${plural(p.plans, 'plan')}, so what it scores decides which places a person is shown.` : 'It is live, and no plan uses it yet.',
    next:
      p.plans === 0
        ? reader.decides
          ? {
              tip: `Put it to work: set up a plan from it in a territory. The plan waits for a superadmin to approve it.`,
              instead: 'Or leave it live: it costs nothing until a plan uses it.',
              actions: [{ label: 'Set up a plan here', to: `/prospecting/setup?market=${p.marketCode}`, primary: true }],
            }
          : { tip: 'Nothing is needed from you. No plan uses it yet; the lead or a superadmin sets one up.' }
        : {
            tip: 'Nothing is needed from you. To change what it looks for, a change is proposed with its evidence and decided under Profile changes; a live profile is never edited in place.',
            instead: reader.decides ? 'If you already know what should change, say so in the chat or open its plans to see what they found.' : undefined,
          },
  };
}

// --- Territories -------------------------------------------------------------------------

const TERRITORY_WHAT: Record<WireTerritory['kind'], string> = { area: 'an area on the map', guide: 'a guide’s list', fair: 'a fair’s exhibitors', country: 'a country searched city by city' };

/** A territory: where we look, and how much of it has been searched. */
export function explainTerritory(t: WireTerritory, who: Who, reader: Reader, extra: { plans?: number } = {}): ItemContext {
  const c = t.coverage;
  const where = [t.city, COUNTRY(t.marketCode)].filter(Boolean).join(', ');
  const plans = extra.plans;
  let tip: string;
  let instead: string | undefined;
  let actions: ItemContext['next']['actions'];
  if (plans === 0 && c.found === 0) {
    tip = reader.decides ? 'No plan searches here yet. Set up a plan to put a profile to work in it; the plan waits for a superadmin to approve it.' : 'Nothing is needed from you. No plan searches here yet; the lead or a superadmin sets one up.';
    if (reader.decides) actions = [{ label: 'Set up a plan here', to: `/prospecting/setup?market=${t.marketCode}`, primary: true }];
  } else if (c.sweptPct !== null && c.sweptPct < 100) {
    tip = `${c.sweptPct}% is searched. Nothing is needed from you: a running plan searches the rest of it, and what it finds appears under Prospects.`;
    instead = 'If a running plan has stopped, see its page: a plan that has ended or reached its budget does not search.';
  } else if (c.fit > c.approved) {
    tip = `${plural(c.fit - c.approved, 'place')} that fit ${c.fit - c.approved === 1 ? 'has' : 'have'} not been approved for writing yet. Read them under Prospects and approve the companies worth it.`;
    actions = [{ label: 'Open Prospects', to: '/prospects' }];
  } else {
    tip = 'Nothing to do here. It is searched and what fit has been approved; new finds appear under Prospects.';
  }
  return {
    subject: { kind: 'territory', title: t.name },
    summary: `${t.name} is ${TERRITORY_WHAT[t.kind]}${where ? ` in ${where}` : ''}. ${c.sweptPct !== null ? `${c.sweptPct}% is searched; ` : ''}${plural(c.found, 'place')} found, ${c.fit} fit, ${c.approved} approved.`,
    need: 'Nothing from you unless a plan should search it.',
    why: 'Plans search a territory, so it is listed with how much of it has been covered.',
    source: { label: t.kind === 'area' ? 'An area a person drew on the map' : t.kind === 'guide' ? `A guide’s list${t.guide ? `: ${t.guide}` : ''}` : t.kind === 'fair' ? `A fair’s exhibitors${t.fairOn ? `, on ${t.fairOn}` : ''}` : 'A country, taken city by city' },
    broughtBy: madeBy(who, t.createdBy, t.kind === 'area' ? 'drew it' : 'named it'),
    owner: who.lead(reader.department),
    involved: withRole(who.agentsOf(reader.department), 'searches it under a plan'),
    knows: who.knowers(reader.department),
    facts: [
      ['Searched', c.sweptPct === null ? null : `${c.sweptPct}%`],
      ['Found / fit / approved', `${c.found} / ${c.fit} / ${c.approved}`],
      ['Contacted', c.found > 0 ? String(c.contacted ?? 0) : null],
      ['Plans in it', plans === undefined ? null : String(plans)],
    ],
    next: { tip, ...(instead ? { instead } : {}), ...(actions ? { actions } : {}) },
    open: { to: `/territories/${t.id}` },
  };
}

// --- Plans ---------------------------------------------------------------------------------

const LOOKS_FOR: Record<NonNullable<WirePlan['looksFor']>, string> = {
  customers: 'It looks for customers to write to.',
  suppliers: 'It looks for suppliers to know of; nobody there is written to.',
  competitors: 'It looks for competitors to file on the market; nobody there is written to.',
};

/**
 * A plan is a profile at work in a territory, at a pace, until a date, within
 * a budget. It is an assignment: nothing searches until a superadmin approves
 * it at the admin door, granting one agent exactly its tools (ADR-0134).
 */
export function explainPlan(p: WirePlan, who: Who, reader: Reader): ItemContext {
  const country = COUNTRY(p.marketCode);
  const title = `${p.segment} in ${p.territory}`;
  const f = p.funnel;
  const agent: Party | null = p.agent ? { name: `${p.agent.givenName} Agent`, kind: 'agent', role: 'runs the plan' } : null;
  const approver = who.actor(p.approvedBy);
  const spentAll = p.spentEur >= p.budgetEur;
  const base = {
    subject: { kind: 'plan', title },
    summary: `A plan that puts the “${p.segment}” profile to work in ${p.territory}, ${country}: up to ${plural(p.perDay, 'place')} a day until ${dayWords(p.until)}, within ${eur(p.budgetEur)}. ${LOOKS_FOR[p.looksFor ?? 'customers']}`,
    source: { label: `Its profile, “${p.segment}”, in ${p.territory}`, to: `/profiles/${p.profileId}` },
    broughtBy: madeBy(who, p.createdBy, 'set it up'),
    owner: who.lead(reader.department),
    knows: who.knowers(reader.department),
    facts: [
      ['Spent', `${eur(p.spentEur)} of ${eur(p.budgetEur)}`],
      ['Found', `${f.found} places, ${f.aboveThreshold} above the bar, ${f.drafted} drafted to`],
      ['Replied / ordered', `${f.replied ?? p.stops?.['replied'] ?? 0} / ${f.ordered ?? 0}`],
      ['Cost per qualified lead', p.costPerQualifiedLead !== null ? eur(p.costPerQualifiedLead) : null],
      ['Approved by', approver ? approver.name : null],
      ['Dry run', p.dryRun?.ranAt ? `Ran ${shortDate(p.dryRun.ranAt)}${p.dryRun.found !== undefined ? `, ${p.dryRun.found} places` : ''}` : p.state === 'waiting' ? 'Not run yet' : null],
    ] as ItemContext['facts'],
    open: { to: `/prospecting/plans/${p.id}` },
  };

  if (p.state === 'waiting') {
    const superadmin = who.superadmin;
    return {
      ...base,
      need: superadmin ? 'Your approval: you name the agent and grant it these tools until the end date, under a token.' : 'A superadmin’s approval. Nothing is searched before it.',
      why: reader.decides ? 'It is your department’s plan, and a plan never searches on its own: it waits for a superadmin.' : 'It is your department’s plan; it waits for a superadmin before anything is searched.',
      involved: undefined,
      next: superadmin
        ? {
            tip: p.dryRun ? 'Read the dry run on its page, then approve: pick the agent, mint the token, and the first search starts within a few minutes. A vendor’s tool must already be admitted.' : 'Run it dry first (one day, nobody contacted, nothing written outside our index), then approve it from its page.',
            instead: 'Or leave it waiting: it costs nothing and searches nothing.',
          }
        : reader.decides
          ? {
              tip: p.dryRun ? 'The dry run is done. Ask a superadmin to approve the plan; there is nothing more you can do to start it.' : 'Run it dry from its page so the superadmin has one day’s result to read, then ask them to approve it.',
              instead: 'Until it is approved nothing is searched, so there is no harm in waiting.',
            }
          : { tip: 'Nothing is needed from you. A superadmin approves the plan, and the department’s lead can run it dry first.', instead: 'Tell the lead if the territory or the pace looks wrong.' },
      task: who.superadmin ? { title: `Approve the plan: ${title}`, from: `/prospecting/plans/${p.id}` } : undefined,
    };
  }
  if (p.state === 'running') {
    const live = {
      ...base,
      involved: agent ? [agent] : withRole(who.agentsOf(reader.department), 'runs the plan'),
    };
    if (!p.grantLive) {
      return {
        ...live,
        need: 'A new grant from a superadmin, or nothing if it should stay stopped.',
        why: 'A plan outlives nothing it was granted: its grant has ended, so it no longer searches.',
        next: who.superadmin
          ? { tip: 'Its grant has ended, so it has stopped searching. Grant it again from Assignments if it should carry on; otherwise leave it stopped.', instead: 'Leaving it stopped costs nothing.', actions: [{ label: 'Open Assignments', to: '/assignments' }] }
          : { tip: 'It has stopped searching because its grant ended. Ask a superadmin to grant it again if you want it to carry on.', instead: 'Otherwise nothing is needed: what it found stays on its page.' },
      };
    }
    if (spentAll) {
      return {
        ...live,
        need: 'A decision on whether more searching is worth another plan.',
        why: 'A plan stops at its budget whatever its pace says, and this one has reached it.',
        next: {
          tip: `It has spent its ${eur(p.budgetEur)} and has stopped searching. Read what it found under Prospects${f.aboveThreshold > 0 ? ` (${f.aboveThreshold} cleared the bar)` : ''}.`,
          instead: reader.decides ? 'If more is wanted, set up a new plan: it needs a superadmin’s approval like any other.' : 'More searching needs a new plan, which the lead sets up.',
          actions: [{ label: 'Open Prospects', to: '/prospects' }],
        },
      };
    }
    return {
      ...live,
      need: 'Nothing from you while it runs; it stops at its budget or its end date.',
      why: `It is running now: ${f.found} places found so far${f.aboveThreshold > 0 ? `, ${f.aboveThreshold} above the bar` : ''}.`,
      next: {
        tip: f.aboveThreshold > 0 ? `Nothing is needed to keep it running. ${plural(f.aboveThreshold, 'place')} cleared the bar: read them under Prospects, and act on the drafts waiting for a person.` : 'Nothing is needed from you now. Its finds appear under Prospects as they are read.',
        instead: 'To stop it early, ask a superadmin to end its assignment.',
        actions: [{ label: 'Open Prospects', to: '/prospects' }],
      },
    };
  }
  return {
    ...base,
    involved: agent ? [agent] : undefined,
    need: 'Nothing from you.',
    why: `It has ended${spentAll ? ' at its budget' : ' at its end date'}, so it no longer searches; what it found stays on its page.`,
    next: {
      tip: 'Nothing to do. Read what it found under Prospects if you have not.',
      instead: reader.decides ? 'To search again, set up a new plan; it needs a superadmin’s approval.' : 'To search again, the lead sets up a new plan.',
      actions: [{ label: 'Open Prospects', to: '/prospects' }],
    },
  };
}
